import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, switchMap, tap, filter } from 'rxjs/operators';
import { PROJECTS_SERVICE } from '../../services/projects/projects.service.base';
import { IProjectsService } from '../../interfaces/services/projects-service.interface';
import { getErrorMessage } from '../../utils/error.utils';
import { ToastService } from '../../services/toast.service';
import * as ProjectsActions from './projects.actions';

@Injectable()
export class ProjectsEffects {
  private actions$ = inject(Actions);
  private projectsService = inject<IProjectsService>(PROJECTS_SERVICE);
  private toastService = inject(ToastService);
  /** Every bookmark requested for the current load, so a cursor cycle cannot keep paging. */
  private requestedBookmarks = new Set<string>();

  loadProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.loadProjects),
      tap(({ bookmark }) => {
        if (!bookmark) {
          this.requestedBookmarks.clear();
          return;
        }
        this.requestedBookmarks.add(bookmark);
      }),
      switchMap(({ bookmark, direction }) => {
        const append = !!bookmark;
        return this.projectsService.getProjects({ bookmark, direction }).pipe(
          map((page) => {
            const nextBookmark = page.continuation.bookmark ?? null;
            // A token already requested in this load will not advance the list, including A → B → A.
            const stalled = !!nextBookmark && this.requestedBookmarks.has(nextBookmark);
            return ProjectsActions.loadProjectsSuccess({
              projects: page.projects,
              hasMoreData: stalled ? false : page.continuation.hasMoreData,
              bookmark: stalled ? null : nextBookmark,
              totalCount: page.continuation.totalCount ?? null,
              append,
              direction,
            });
          }),
          catchError((error) =>
            of(
              ProjectsActions.loadProjectsFailure({
                error: getErrorMessage(error),
                append,
              })
            )
          )
        );
      })
    )
  );

  /** Keeps requesting Bookmark pages after any caller loads the list, including deep links. */
  continueProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.loadProjectsSuccess),
      filter((action) => this.shouldContinue(action)),
      map((action) =>
        ProjectsActions.loadProjects({
          bookmark: action.bookmark,
          direction: action.direction,
        })
      )
    )
  );

  loadProjectsAppendFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.loadProjectsFailure),
        tap(({ error, append }) => {
          if (append) {
            this.toastService.error(error, 'Error');
          }
        })
      ),
    { dispatch: false }
  );

  addProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.addProject),
      mergeMap(({ name }) =>
        this.projectsService.addProject(name).pipe(
          map((project) => ProjectsActions.addProjectSuccess({ project })),
          catchError((error) => of(ProjectsActions.addProjectFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.deleteProject),
      mergeMap(({ id }) =>
        this.projectsService.deleteProject(id).pipe(
          map(() => ProjectsActions.deleteProjectSuccess({ id })),
          catchError((error) => of(ProjectsActions.deleteProjectFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  addProjectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.addProjectSuccess),
        tap(({ project }) => {
          this.toastService.success(`Project "${project.name}" added successfully.`, 'Success');
        })
      ),
    { dispatch: false }
  );

  deleteProjectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.deleteProjectSuccess),
        tap(() => {
          this.toastService.success('Project deleted successfully.', 'Success');
        })
      ),
    { dispatch: false }
  );

  addProjectFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.addProjectFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );

  private shouldContinue(
    action: ReturnType<typeof ProjectsActions.loadProjectsSuccess>
  ): boolean {
    if (!action.hasMoreData || !action.bookmark) {
      return false;
    }

    if (this.requestedBookmarks.has(action.bookmark)) {
      return false;
    }

    // Record before the next load runs so a second emission of this page cannot slip through.
    this.requestedBookmarks.add(action.bookmark);
    return true;
  }

  deleteProjectFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.deleteProjectFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );
}
